/**
 * Template Editor Component
 * Full editor for prompt template with sections management
 */

import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Save, Star, AlertCircle, List, Code2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SectionList } from './SectionList';
import { SectionEditor } from './SectionEditor';
import { VisualPromptEditor } from './VisualPromptEditor';
import type { PromptTemplate, PromptSection, UpdateTemplateRequest, SectionOrderItem } from '@/types/promptTemplates';

type EditorViewMode = 'list' | 'visual';

interface TemplateEditorProps {
  template: PromptTemplate;
  onBack: () => void;
  onSave: (data: UpdateTemplateRequest) => Promise<void>;
  onSetDefault: () => Promise<void>;
  onAddSection: (section: PromptSection) => Promise<void>;
  onUpdateSection: (sectionId: string, section: PromptSection) => Promise<void>;
  onDeleteSection: (sectionId: string) => Promise<void>;
  onReorderSections: (orders: SectionOrderItem[]) => Promise<void>;
  isSaving?: boolean;
}

export function TemplateEditor({
  template,
  onBack,
  onSave,
  onSetDefault,
  onAddSection,
  onUpdateSection,
  onDeleteSection,
  onReorderSections,
  isSaving,
}: TemplateEditorProps) {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || '');
  const [separator, setSeparator] = useState(template.separator);
  const [sections, setSections] = useState<PromptSection[]>(template.sections);
  const [editingSection, setEditingSection] = useState<PromptSection | null>(null);
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState<EditorViewMode>('visual');

  useEffect(() => {
    setSections(template.sections);
  }, [template.sections]);

  useEffect(() => {
    const changed =
      name !== template.name ||
      description !== (template.description || '') ||
      separator !== template.separator;
    setHasChanges(changed);
  }, [name, description, separator, template]);

  const handleSave = async () => {
    await onSave({
      name,
      description: description || null,
      separator,
    });
    setHasChanges(false);
  };

  const handleAddSection = async (section: PromptSection) => {
    await onAddSection(section);
    setIsAddingSection(false);
  };

  const handleUpdateSection = async (section: PromptSection) => {
    if (editingSection) {
      await onUpdateSection(editingSection.id, section);
      setEditingSection(null);
    }
  };

  const handleToggleSection = async (sectionId: string, enabled: boolean) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      await onUpdateSection(sectionId, { ...section, enabled });
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      await onDeleteSection(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  const handleReorder = async (newSections: PromptSection[]) => {
    setSections(newSections);
    const orders: SectionOrderItem[] = newSections.map(s => ({
      id: s.id,
      order: s.order,
    }));
    await onReorderSections(orders);
  };

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold text-gray-900">{template.name}</h1>
                  {template.is_default && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                      <Star className="w-3 h-3" />
                      Default
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {sections.length} sections | Last updated: {new Date(template.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!template.is_default && (
                <Button
                  variant="secondary"
                  onClick={onSetDefault}
                  loading={isSaving}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Set as Default
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!hasChanges}
                loading={isSaving}
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Template Settings */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Template Settings</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Separator
                </label>
                <select
                  value={separator}
                  onChange={e => setSeparator(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="\n\n">Double newline (\n\n)</option>
                  <option value="\n">Single newline (\n)</option>
                  <option value="\n---\n">Horizontal rule (---)</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Describe this template..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Prompt Sections
              </h2>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('visual')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'visual'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Code2 className="w-4 h-4" />
                    Visual
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'list'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    List
                  </button>
                </div>
                {viewMode === 'list' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setIsAddingSection(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Section
                  </Button>
                )}
              </div>
            </div>

            {viewMode === 'list' ? (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Drag sections to reorder. Sections are combined in order to build the system prompt.
                </p>
                <SectionList
                  sections={sections}
                  onEdit={setEditingSection}
                  onDelete={setDeleteConfirm}
                  onToggle={handleToggleSection}
                  onReorder={handleReorder}
                />
              </>
            ) : (
              <VisualPromptEditor
                sections={sections}
                separator={separator}
                onAddSection={() => setIsAddingSection(true)}
                onEditSection={setEditingSection}
                onDeleteSection={setDeleteConfirm}
                onToggleSection={handleToggleSection}
                onReorder={handleReorder}
              />
            )}
          </div>
        </div>
      </div>

      {/* Section Editor Dialog */}
      <SectionEditor
        isOpen={isAddingSection || editingSection !== null}
        section={editingSection}
        onClose={() => {
          setIsAddingSection(false);
          setEditingSection(null);
        }}
        onSave={editingSection ? handleUpdateSection : handleAddSection}
        isSubmitting={isSaving}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Section</h3>
                <p className="text-gray-600 mt-1">
                  Are you sure you want to delete the section "{deleteConfirm}"?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete} loading={isSaving}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
